import { get, cloneDeep, merge, isEmpty } from 'lodash';
import { mapActions } from 'vuex';

export default {
    props: {
        purchasePageCTA: {
            type: [Object, Boolean],
            default: false,
        },
        singleSlice: {
            type: Boolean,
            default: false,
        },
        settings: {
            type: Object,
            default: null,
        },
        information: {
            type: Array,
            default: null,
        },
        productCards: {
            type: Object,
            default: null,
        },
    },
    computed: {
        ott() {
            return this.searchParams.get('ott');
        },
        entryPoint() {
            return this.searchParams.get('ep');
        },
        machineProperty() {
            return this.searchParams.get('mp');
        },
        quantityNextPC() {
            if (this.inapp.pc > 3) return 2;
            return this.inapp.pc > 1 ? 1 : 0;
        },
        quantityNextEssentialPC() {
            if (this.inapp.pc >= 3) return 2;
            return this.inapp.pc >= 1 ? 1 : 0;
        },
        quantityRenew() {
            if (this.inapp.pc > 3) return 5;
            return this.inapp.pc > 1 ? 3 : 1;
        },
        notifications() {
            return this.content?.inapp?.notifications || {};
        },
        hasMaximumLicense() {
            if (this.inapp.pc > 5) return true;
            if (this.inapp.pc === 5 && this.inapp.cloud > 5120) return true;
            if (this.inapp.pc === 5 && this.inapp.cloud === 5120 && this.isPremium) return true;
            return false;
        },
        hasNotificationRenewOnly() {
            if (['nocloud', 'perpetual'].includes(this.inapp.license)) return false;
            return this.inapp.pc < 5 && this.inapp.cloud > 5120;
        },
        isUpgrade() {
            const isATI = ['ati_1pc', 'ati_3pc', 'ati_5pc'].includes(this.machineProperty);
            if (this.inapp.cloud_trial && isATI) return false;

            const isTrial = ['ati_trial_active', 'ati_trial_expired'].includes(this.machineProperty);
            if (!this.inapp.license && isTrial) return false;

            return !isEmpty(this.inapp);
        },
        isRenew() {
            if (this.entryPoint !== 'in_app_store_renew') return false;
            return this.inapp.license !== 'perpetual';
        },
        isPremium() {
            return this.inapp.license === 'premium';
        },
        isAdvanced() {
            return this.inapp.license === 'advanced';
        },
        isEssential() {
            return this.inapp.license === 'nocloud';
        },
        isPerpetualOnly() {
            return Boolean(this.inapp.only_perpetual);
        },
        isSingle() {
            return this.singleSlice || this.content?.singleSlice || false;
        },
        isPriceForMonth() {
            return Boolean(this.content?.productCards?.isPriceForMonth);
        },
        pageTitle() {
            return this.title || this.content?.title || '';
        },
        pageLead() {
            return this.lead || this.content?.lead || '';
        },
        pageBodyText() {
            return this.bodyText || this.content?.bodyText || '';
        },
        className() {
            return {
                ...this.basicClassName,
                's-purchasing-single': this.isSingle,
            };
        },
        message() {
            return (this.promo.isPromoActive && this.promo) || this.cta?.message || {};
        },
    },
    created() {
        this.setPromo();
    },
    mounted() {
        if (!this.content) return;

        this.products = cloneDeep(this.pageProductCards);
        if (this.information) {
            this.products.information = this.information;
        }
        if (this.promo.isPromoActive) {
            if (Number.isInteger(this.promo?.selectedPC)) {
                this.products.items.forEach((product, i) => {
                    this.products.items[i].pc.selected = this.promo.selectedPC;
                });
            }
            if (Number.isInteger(this.promo?.selectedLicense)) {
                this.products.selected = this.promo.selectedLicense;
            }
        }
        window.addEventListener('resize', this.resize);

        this.initInapp();
    },
    methods: {
        ...mapActions({
            getUserCloud: 'user/getUserCloud',
        }),

        async initInapp() {
            this.searchParams = new URL(window.location.href).searchParams;

            if (!this.entryPoint) return;
            if (!this.machineProperty) return;
            if (!this.content?.inapp?.ep?.[this.entryPoint]) return;
            if (!this.content?.inapp?.mp?.includes(this.machineProperty)) return;

            document.body.classList.add('is-inapp');

            if (['in_app_store_trial', 'in_app_store_license'].includes(this.entryPoint)) {
                this.setSelectedDefault();
                return;
            }

            await this.userCloud();

            if (this.isRenew) {
                this.renew();
                return;
            }

            if (!this.isUpgrade) return;

            if (this.hasMaximumLicense) {
                this.notification = this.notifications['maximum-license'];
                return;
            }

            if (this.hasNotificationRenewOnly) {
                this.notification = this.notifications['renew-only'];
                this.isLoading = true;
                return;
            }

            if (['in_app_store_av', 'in_app_store_cloud'].includes(this.entryPoint)) {
                this.setSelectedDefault();
                this.products.items[0].isNotAvailable = true;
                return;
            }

            this.setSelectedDefault();
            this.setLicenseIDs();
            this.setQuantityPC();
            this.setSelectedPC();
            this.setSelectedCloud();
            this.setButtonText();
            this.hideProducts();
        },
        async userCloud() {
            try {
                this.isLoading = true;
                const response = await this.getUserCloud(this.ott || '');
                if (response.err) return;
                this.inapp = response;
            } catch (error) {
                this.inapp = {};
            } finally {
                this.isLoading = false;
            }
        },
        cloudList(productID) {
            const cloud = {
                1: [50, 250, 500],
                2: [1024, 2048, 3072, 4096, 5120],
            };
            return cloud[productID] || [...cloud[1], ...cloud[2]];
        },
        getNextCloudValue(productID) {
            const cloudList = this.cloudList(productID);
            const index = cloudList.findIndex((item) => item > this.inapp.cloud);
            return index > -1 ? index : cloudList.length - 1;
        },
        hideProducts() {
            if (this.isPremium) {
                this.products.items[0].isNotAvailable = true;
                this.products.items[1].isNotAvailable = true;
                return;
            }
            if (this.isAdvanced) {
                this.products.items[0].isNotAvailable = true;
                if (this.inapp.cloud > 250 && this.inapp.pc > 4) {
                    this.products.items[1].isNotAvailable = true;
                }
                if (this.inapp.cloud > 500) {
                    this.products.items[1].isNotAvailable = true;
                }
                return;
            }
            if (this.isEssential) {
                this.products.items[0].isNotAvailable = this.inapp.pc > 4;
            }
        },
        renew() {
            if (this.inapp.license === 'no-cloud') {
                const essential = this.content?.inapp?.licenses?.essential || [];
                const license = essential.find((item) => item.quantity === this.quantityRenew);
                if (!license) return;
                this.isLoading = true;
                this.content.lead = '';
                this.content.title = '';
                const licenseData = this.licenseData({ cleverBridgeCartID: license.id });
                this.proceedToCheckout(0, licenseData);
                return;
            }

            if (this.inapp.unlimited === 1) {
                if (this.inapp.cloud_used > 5120) {
                    this.notification = this.notifications['unlimited-renew'];
                    return;
                }
                const unlimited = this.content?.inapp?.licenses?.unlimited || [];
                const license = unlimited.find((item) => item.quantity === this.quantityRenew);
                if (!license) return;
                this.getUnlimLink();
            }
        },
        setQuantityPC() {
            if (!this.isPerpetualOnly) return;
            if (!['ati_1pc', 'ati_3pc', 'ati_5pc'].includes(this.machineProperty)) return;
            this.inapp.pc = parseInt(this.machineProperty.match(/\d+/), 10);
        },
        setLicenseIDs() {
            if (this.entryPoint !== 'in_app_store_upgrade') return;
            const type = this.inapp.license === 'perpetual' ? 'perpetual' : 'subscription';
            const content = this.content?.inapp?.ep?.[this.entryPoint] || {};
            const products = content[type]?.products || {};
            this.products = merge({}, this.products, products);

            if (this.isEssential) {
                const productsEssential = content.essential?.products?.[this.inapp.pc] || {};
                this.products = merge({}, this.products, productsEssential);
            }

            if (this.isPerpetualOnly) {
                const productsPerpetualOnly = content.perpertualOnly?.products || {};
                this.products = merge({}, this.products, productsPerpetualOnly);
            }
        },
        setSelectedDefault() {
            this.products.items.forEach((_product, i) => {
                this.products.items[i].pc.selected = 0;
                this.products.items[i].cloud.selected = 0;
            });
        },
        setSelectedPC() {
            this.products.items.forEach((_product, i) => {
                const isMaximumPremium = this.isPremium && this.inapp.cloud > 4096;
                const isMaximumAdvanced = this.isAdvanced && this.inapp.cloud >= 500;
                const isMaximum = isMaximumPremium || isMaximumAdvanced;
                this.products.items[i].pc.selected = isMaximum ? 1 : this.quantityNextPC;
            });
            if (this.isEssential) {
                this.products.items[0].pc.selected = this.quantityNextEssentialPC;
            }
            if (this.isAdvanced) {
                if (this.inapp.cloud > 250 && this.inapp.pc >= 3) {
                    this.products.items[1].pc.selected = 2;
                    this.products.items[2].pc.selected = 2;
                }
            }
            if (this.isPremium) {
                if (this.inapp.cloud > 4096 && this.inapp.pc >= 3) {
                    this.products.items[2].pc.selected = 2;
                }
            }
        },
        setSelectedCloud() {
            const index = this.isPremium ? 2 : 1;
            this.products.items[index].cloud.selected = this.getNextCloudValue(index);
            if (this.isAdvanced) {
                const MAX_CLOUD_SIZE_FOR_UPDATE = 500;
                if (this.inapp.cloud < MAX_CLOUD_SIZE_FOR_UPDATE) return;
                this.products.items[1].cloud.selected = 2;
            }
        },
        setButtonText() {
            this.products.items.forEach((_product, i) => {
                this.products.items[i].button.text = 'Upgrade';
            });
        },
        setPromo() {
            this.cta = {
                ...this.productCTA({ isPurchase: true }),
                ...this.purchasePageCTA,
            };
            this.coupon = this.$route.query?.coupon || '';
            if (this.coupon && this.activeCoupon?.discount) {
                const license = this.activeCoupon.selectedLicense;
                const list = this.activeCoupon.cleverBridgeCartList || '';
                this.promo = {
                    ...this.activeCoupon,
                    isPurchase: true,
                    isPromoActive: true,
                    cleverBridgeCartList: list.split(',').map(Number),
                    selectedLicense: [1, 2, 3].includes(license) ? license - 1 : 0,
                    selectedPC: { 1: 0, 3: 1, 5: 2 }[this.activeCoupon?.selectedPC || 1],
                };
            } else {
                const promo = { ...this.cta.promo, isPurchase: true };
                const hasPromoPrices = this.checkPromo(promo);
                const isPromoActive = !this.cta.doNotApplyPromo && hasPromoPrices;
                this.promo = { ...promo, isPromoActive };
            }
        },
        licenseCart(license) {
            if (!license.selector) return license.cleverbridge_id;
            return `s${license.selector}:${license.cleverbridge_id}`;
        },
        proceedToCheckout(index, current) {
            const options = this.storeOptionsSURL({
                shopId: 882,
                segment: 'consumer',
                params: { scope: 'checkout' },
            });
            if (this.isRenew || this.isUpgrade) {
                const isTargetRenew = this.isRenew && this.inapp.unlimited !== 1;
                options.targetPathname = 'purl-consumer-inapp';
                options.params['x-target'] = isTargetRenew ? 'renew' : 'upgrade';
                options.params.lockcart = true;
            }
            const license = current || this.findLicense(index);
            options.params.cart = this.licenseCart(license);
            if (license.coupon) options.params.coupon = license.coupon;
            if (this.hasCoupon(license)) options.params.coupon = this.coupon;
            this.redirectToCleverbridge(options);
            this.tryEcommerceSendEvent(index, 'add', 'Proceed to checkout', license);
        },
        cartID(index) {
            const product = this.products.items[index];
            const pc = product.pc.selected || 0;
            const cloud = product.cloud ? product.cloud.selected : null;
            return get(product.cleverBridgeCartID, [pc, cloud], product.cleverBridgeCartID[pc]);
        },
        classNameItem(name, item) {
            return {
                [`${name}-item`]: item,
                [`${name}-glyph`]: item.glyph,
                [`${name}-plus`]: item === '+',
                [`${name}-title`]: item.isTitle,
                [`${name}-text-blue`]: item.isTextBlue,
                [`${name}-glyph-blue`]: item.isGlyphBlue,
                [`${name}-bullet`]: !item.isTitle && !item.glyph && item !== '+',
            };
        },
        findLicense(index) {
            const cleverBridgeCartID = this.cartID(index);
            return this.licenseData({ cleverBridgeCartID, promo: this.promo });
        },
        hasCoupon(license) {
            if (!this.activeCoupon?.discount) return false;
            const list = this.activeCoupon.cleverBridgeCartList || '';
            return (!list || list.includes(license.cleverbridge_id));
        },
        productPriceLabel(index) {
            const license = this.findLicense(index);
            if (!license.discount_percentage) return '';
            return (this.promo?.label || '').replace('XX', Math.round(license.discount_percentage));
        },
        onChange(index, type, value) {
            const product = this.products.items[index][type];
            if (product.selected === value) return;
            this.tryEcommerceSendEvent(index, 'remove', 'Remove from cart');
            product.selected = value;
            if (type === 'pc') {
                const cloud = this.products.items[index].cloud.selected;
                if (this.isHiddenOptionCloud(index, cloud)) {
                    this.products.items[index].cloud.selected = this.getNextCloudValue(index);
                }
            }
            this.tryEcommerceSendEvent(index, 'add', 'Add to cart');
        },
        tryEcommerceSendEvent(index, type, eventAction, current) {
            const license = current || this.findLicense(index);
            const event = {
                eventAction,
                eventLabel: license.name || '',
                ecommerce: { type, license: license.cleverbridge_id },
            };
            this.ecommerceSendEvent({ event, license });
        },
    },
};
